import { forwardRef, useCallback, useEffect, useMemo } from 'react';

import classnames from 'classnames';

import { Button, ButtonElementTypes } from '@picsart/design-system/components/Button';
import { setStorage } from '@picsart/growth-rc/services/browserStorage';
import { useTranslator } from '@picsart/rc/services/localization';

import { uploaderButtonTestId } from 'redesignedComponents/organisms/CTA/testIds';

import useAnalytics from 'services/Analytics';

import { ButtonTypes } from 'types/global';

import { AnalyticEventTypes, ButtonColorOption } from 'constants/enums';
import { CTA_TEXT } from 'constants/main';

import { ButtonWrapperProps } from './types';

import useStyles from './styles';

const ButtonWrapper = forwardRef(
  ({
    buttonLabel,
    analyticsProps,
    color,
    buttonTag,
    className,
    cmsId,
    index,
    buttonColorOption,
    tookUrlFromCampaign,
    ...props
  }: ButtonWrapperProps) => {
    const translate = useTranslator();
    const isHyperLink = buttonColorOption === ButtonColorOption.Hyperlink;
    const styles = useStyles({ isHyperLink });
    const { fireEvent } = useAnalytics();
    const buttonText = useMemo(
      () => (typeof buttonLabel === 'string' ? translate(buttonLabel) : buttonLabel),
      [buttonLabel, translate],
    );

    useEffect(() => {
      if (tookUrlFromCampaign) {
        const interValRef = setInterval(() => {
          const linkFromCampaign = (window as any)?.AF_SMART_SCRIPT_RESULT?.clickURL;
          if (linkFromCampaign) {
            // eslint-disable-next-line no-console
            console.log((window as any)?.AF_SMART_SCRIPT_RESULT);

            (window as any)
              ?.createImpressionsLink(linkFromCampaign)
              .then((impressionLink: string) => {
                // eslint-disable-next-line no-console
                console.log('Impression Link: ', impressionLink);
              })
              .catch(err => {
                console.error('Failed to create impression link: ', err);
              });

            setTimeout(() => {
              (window as any)?.AF_SMART_SCRIPT?.fireImpressionsLink();
              // eslint-disable-next-line no-console
              console.log('Impression fired');
            }, 1000);

            clearInterval(interValRef);
          }
        }, 1000);

        return () => {
          clearInterval(interValRef);
        };
      }
      return undefined;
    }, [tookUrlFromCampaign]);

    const buttonClickHandler = useCallback(
      e => {
        if (!tookUrlFromCampaign) {
          fireEvent(AnalyticEventTypes.page_click, {
            ...analyticsProps,
          });
        }

        setStorage(CTA_TEXT, analyticsProps?.item_name ? analyticsProps?.item_name : e.target.id);

        if (tookUrlFromCampaign) {
          const interValRef = setInterval(() => {
            const linkFromCampaign = (window as any)?.AF_SMART_SCRIPT_RESULT?.clickURL;
            if (linkFromCampaign) {
              fireEvent(AnalyticEventTypes.page_click, {
                ...analyticsProps,
                group: 'main',
                section: 'main',
                name: (buttonLabel as string)?.replaceAll(' ', '_')?.toLowerCase(),
              });
              clearInterval(interValRef);
              window.location.href = linkFromCampaign;
            }
          }, 1000);
        }

        if (props?.onClick) {
          props.onClick(e);
        }
      },
      [analyticsProps, buttonLabel, fireEvent, props, tookUrlFromCampaign],
    );

    return (
      <>
        <Button
          {...props}
          onClick={buttonClickHandler}
          className={classnames(styles.button, className)}
          data-pulse-name={buttonText}
          aria-label={props['aria-label'] || buttonText.toString()}
          id={
            index
              ? `${index}_${cmsId}_${ButtonTypes?.[props?.tag]}`
              : `${cmsId}_${ButtonTypes?.[props?.tag]}`
          }
          data-pulse-clickable
        >
          {buttonTag === ButtonElementTypes.Upload ? (
            <span data-testid={uploaderButtonTestId}>{buttonText}</span>
          ) : (
            buttonText
          )}
        </Button>
      </>
    );
  },
);
ButtonWrapper.displayName = 'ButtonWrapper';
export default ButtonWrapper;
